import apiCall from "../../api";
import React, { useEffect, useState, memo, useRef } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import Table from "react-bootstrap/Table";
import { useTranslation } from "react-i18next";
import { getBankDetails } from "../../redux/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { specialCharacterTest } from "../../common/Regex";
import { BankOTPModal } from "./BankOTPModal";
import { mobileScroll } from "../../common/FunctionUtils";
import { LOCALES } from "../../common/constants";

interface NewBankInformationProps {
  setShowBankInfoPopup: (value: boolean) => void;
  showBankInfoPopup: boolean;
  isPopup?: boolean;
}

const NewBankInformation: React.FC<NewBankInformationProps> = ({ 
  setShowBankInfoPopup, 
  showBankInfoPopup, 
  isPopup = false 
}) => {
  const dispatch = useDispatch();
  const { isLoading, bankDetails } = useSelector((details: any) => details.auth);
  
  const { t, i18n } = useTranslation();
  const language = i18n.language || localStorage.getItem("i18nextLng") || "en-us";

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hideNote, setHideNote] = useState(false);

  const inputRefs = useRef<any>([]);
  const [OTPModal, setOTPModal] = useState<boolean | number>(false);
  const [OTPField, setOTPField] = useState(new Array(4).fill(""));
  const [counter, setCounter] = useState(300);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const bankName = watch("bank");
  const ibanNumber = watch("ibanNumber");
  const bicNumber = watch("bicNumber");
  const clearingNumber = watch("clearingNumber");
  const accountNumber = watch("accountNumber");
  const otherBankName = watch("OtherBank");

  const isIbanBank = ["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName);
  const isBankSelected = !!bankName && bankName !== t("Select a bank") && bankName !== "Select a bank";
  const isSaveDisabled = (() => {
    if (!isBankSelected) return true;
    if (bankName === "Other Bank" && !otherBankName?.trim()) return true;
    if (isIbanBank) return !ibanNumber?.trim() || !bicNumber?.trim() || !!errors.ibanNumber || !!errors.bicNumber;
    return !String(clearingNumber || '').trim() || !String(accountNumber || '').trim() || !!errors.clearingNumber || !!errors.accountNumber;
  })();

  useEffect(() => {
    dispatch(getBankDetails() as any);
  }, [dispatch]);

  useEffect(() => {
    bankDetails.forEach((data: any) => {
      reset({ ...data });
    });
    setData(bankDetails[0]);
  }, [bankDetails, reset]);

  const [toggle, setToggle] = useState(false);
  const [bankValues, setBankValues] = useState<any>();

  const generateOTP = async () => {
    setLoading(true);
    try {
      const URL = "/settings/generate-otp";

      const body = {
        type: 3,
        email: "",
        lang: language,
      };

      const result = await apiCall.post(URL, body);
      if (result?.status === 200) {
        setLoading(false);
        toast.success(String(t("Email has been successfully sent to your email")));
        setOTPModal(true);
      }
    } catch (error: any) {
      setLoading(false);
      toast.error(String(t(error?.response?.data?.message)));
    }
  };

  const onSubmit = async (values: any) => {
    setToggle(false);
    setBankValues(values);
    generateOTP();
  };

  useEffect(() => {
    if (["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName)) {
      setHideNote(true);
    } else {
      setHideNote(false);
    }
  }, [bankName]);

  const handleClose = () => setToggle(false);

  const handleShow = () => {
    setToggle(true);
  };

  let isValidated: boolean;
  const validateClearingNumber = (value: string) => {
    isValidated = specialCharacterTest(value);
    if (!isValidated) {
      setError("clearingNumber", {
        type: "manual",
        message: `${t(
          "Please do not enter hyphen and any other special character"
        )}`,
      });
    } else {
      clearErrors("clearingNumber");
    }
    return true;
  };

  const validateAccountNumber = (value: string) => {
    isValidated = specialCharacterTest(value);
    if (!isValidated) {
      setError("accountNumber", {
        type: "manual",
        message: `${t(
          "Please do not enter hyphen and any other special character"
        )}`,
      });
    } else {
      clearErrors("accountNumber");
    }
    return true;
  };

  const handleChangeOTP = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isNaN(Number(value))) return;

    const newOtp = [...OTPField];

    newOtp[index] = value.substring(value.length - 1);
    setOTPField(newOtp);

    if (value && index < 4 - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
    if (value && index === 3) {
      const combinedOTP = newOtp.join("");

      try {
        setLoading(true);
        const URL = "/settings/save-customer-salary";
        const payload = {
          id: data?.id,
          ...bankValues,
          incomeTax: bankValues.incomeTax,
          accountNumber: bankValues.accountNumber
            ? bankValues.accountNumber
            : "",
          clearingNumber: bankValues.clearingNumber
            ? bankValues.clearingNumber
            : "",
          bank:
            bankValues.bank === "Other Bank"
              ? bankValues.OtherBank
              : bankValues.bank,
          code: combinedOTP,
        };
        const result = await apiCall.put(URL, payload);
        if (result?.status === 200) {
          await dispatch(getBankDetails() as any);
          toast.success(String(t("Your bank details has been added.")));
          setOTPModal(false);
          setShowBankInfoPopup(false);
          setOTPField(new Array(4).fill(""));
        } else {
          toast.error(String(t("Something went wrong please try again.")));
        }
      } catch (err: any) {
        if (err?.response?.data) {
          toast.error(String(t(err?.response?.data?.message)));
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const resendOTP = () => {
    setOTPField(new Array(4).fill(""));
    generateOTP();
  };

  useEffect(() => {
    mobileScroll();
  }, []);

  return (
    <div className={`${!showBankInfoPopup ? "mt-5" : ""}`} style={{
      maxWidth: showBankInfoPopup ? "100%" : "900px",
      margin: showBankInfoPopup ? "0" : "0 auto"
    }}>
      {(isLoading || loading) && <Loader />}

      {!isPopup && toggle && (
        <Modal show={toggle} onHide={handleClose} animation={false}>
          <Modal.Header closeButton>
            <Modal.Title>{t("Bank Information")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t("Are you sure you want to update your bank account information?")}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              {t("No")}
            </Button>
            <Button
              variant="primary"
              className="theme_btn"
              onClick={handleSubmit(onSubmit)}
            >
              {t("Yes")}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {isPopup && toggle && (
        <div style={{
          padding: "40px 32px",
          textAlign: "center",
          position: "relative",
          background: "#ffffff"
        }}>
          <div style={{
            background: "rgb(97 75 184)",
            borderRadius: "50%",
            width: 72,
            height: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            boxShadow: "0 4px 12px rgba(97, 75, 184, 0.25)"
          }}>
            <svg width="36" height="36" fill="#ffffff" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
            </svg>
          </div>
          <h2 style={{
            fontWeight: 700, 
            fontSize: "24px", 
            marginBottom: "12px", 
            color: "#1a202c",
            fontFamily: "CircularStd-Bold, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          }}>
            {t("Bank Information")}
          </h2>
          <p style={{
            fontSize: "15px", 
            color: "#718096", 
            marginBottom: "32px",
            lineHeight: "1.6",
            fontFamily: "CircularStd-Book, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          }}>
            {t("Are you sure you want to update your bank account information?")}
          </p>
          <div style={{display: "flex", justifyContent: "center", gap: 12}}>
            <Button
              variant="outline-secondary"
              style={{
                minWidth: 120,
                height: 44,
                borderRadius: "25px",
                fontWeight: 600,
                fontSize: "14px",
                border: "2px solid #e2e8f0",
                color: "#4a5568",
                backgroundColor: "transparent",
                transition: "all 0.2s ease",
                fontFamily: "CircularStd-Medium, sans-serif"
              }}
              onClick={handleClose}
              // onMouseEnter={(e) => {
              //   e.currentTarget.style.backgroundColor = "#f7fafc";
              //   e.currentTarget.style.borderColor = "#cbd5e0";
              // }}
              // onMouseLeave={(e) => {
              //   e.currentTarget.style.backgroundColor = "transparent";
              //   e.currentTarget.style.borderColor = "#e2e8f0";
              // }}
            >
              {t("No")}
            </Button>
            <Button
              variant="primary"
              style={{
                minWidth: 120,
                height: 44,
                borderRadius: "25px",
                fontWeight: 600,
                fontSize: "14px",
                background: "rgb(97 75 184)",
                border: "none",
                // boxShadow: "0 4px 12px rgba(102, 126, 234, 0.35)",
                // transition: "all 0.2s ease",
                fontFamily: "CircularStd-Medium, sans-serif",
              }}
              onClick={handleSubmit(onSubmit)}
              // onMouseEnter={(e) => {
              //   e.currentTarget.style.transform = "translateY(-1px)";
              //   e.currentTarget.style.boxShadow = "0 6px 16px rgba(102, 126, 234, 0.45)";
              // }}
              // onMouseLeave={(e) => {
              //   e.currentTarget.style.transform = "translateY(0)";
              //   e.currentTarget.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.35)";
              // }}
            >
              {t("Yes")}
            </Button>
          </div>
        </div>
      )}
     
      {((!toggle && !(OTPModal === true) && isPopup) || !isPopup) && (
        <div>
          <div style={{padding: showBankInfoPopup ? "0" : "0"}}>
            <form onSubmit={handleSubmit(handleShow)} style={{
              backgroundColor: "#ffffff",
              borderRadius: showBankInfoPopup ? "0" : "12px",
              padding: showBankInfoPopup ? "0" : "32px"
            }}>
              {/* Title and Description */}
              {showBankInfoPopup && (
                <div style={{marginBottom: "24px"}}>
                  <p style={{ 
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#4a5568",
                    margin: 0,
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    {t(
                      "It looks like you forgot to enter your bank details. To complete your setup and receive payments, you can securely enter them here to receive payments and complete your setup."
                    )}
                  </p>
                </div>
              )}

              {!showBankInfoPopup && (
                <div style={{marginBottom: "28px"}}>
                  <h4 style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#1a202c",
                    marginBottom: "8px",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    {t("Salary and allowance")}
                  </h4>
                  <h3 style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#2d3748",
                    marginBottom: "8px",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    {t("Bank information")}
                  </h3>
                  <p style={{ 
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#718096",
                    margin: 0,
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    {t(
                      "We expressly disclaim any responsibility for discrepancies or inaccuracies in the bank account details furnished by the user."
                    )}
                  </p>
                </div>
              )}

              {/* Bank Select */}
              <div style={{marginBottom: "20px"}}>
                <label htmlFor="inputAddress" style={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#4a5568",
                  marginBottom: "8px",
                  display: "block",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}>{t("Bank")}</label>
                <select
                  className="form-control"
                  {...register("bank")}
                  style={{
                    height: "48px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "14px",
                    color: "#2d3748",
                    padding: "0 16px",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    backgroundColor: "#ffffff",
                    cursor: "pointer",
                    appearance: "none",
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234a5568' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 16px center",
                    transition: "all 0.2s ease"
                  }}
                >
                    <option> {t("Select a bank")} </option>
                    <option value="Handelsbanken"> {t("Handelsbanken")} </option>
                    <option value="Nordea social security number">
                      {" "}
                      {t("Nordea social security number")}
                    </option>
                    <option value="Nordea Account number">
                      {" "}
                      {t("Nordea Account number")}{" "}
                    </option>
                    <option value="Revolut Bank"> Revolut Bank </option>
                    <option value="Swedbank"> Swedbank </option>
                    <option value="SEB"> SEB </option>
                    <option value="Foreign bank BIC / IBAN">
                      {" "}
                      {t("Foreign bank BIC / IBAN")}{" "}
                    </option>
                    <option value="Other Bank"> {t("Other Bank")} </option>
                    {data?.bank &&
                      !["Handelsbanken",
                        "Nordea social security number",
                        "Nordea Account number",
                        "Revolut Bank",
                        "Swedbank",
                        "SEB",
                        "Foreign bank BIC / IBAN"].includes(data?.bank) && (
                      <option value={data?.bank}> {data?.bank} </option>
                    )}
                  </select>
                </div>

                {bankName === "Other Bank" && (
                  <div style={{marginBottom: "20px"}}>
                    <label
                      htmlFor="Bank-Details"
                      style={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#4a5568",
                        marginBottom: "8px",
                        display: "block",
                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                      }}
                    >
                      {t("Bank Name")}
                    </label>
                    <input
                      {...register("OtherBank")}
                      id="Bank-Details"
                      autoFocus
                      placeholder={t("Your Bank Name Here")}
                      type="text"
                      className="form-control"
                      style={{
                        height: "48px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        fontSize: "14px",
                        color: "#2d3748",
                        padding: "0 16px",
                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        transition: "all 0.2s ease"
                      }}
                    />
                  </div>
                )}

                {/* Input Fields Row */}
                <div style={{display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap"}}>
                  {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) ? (
                    <>
                      <div style={{flex: "1 1 calc(50% - 8px)", minWidth: "200px"}}>
                        <label
                          htmlFor="ibanNumber"
                          style={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#4a5568",
                            marginBottom: "8px",
                            display: "block",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                          }}
                        >
                          {t("IBAN Number")}
                        </label>
                        <input
                          autoFocus
                          type="text"
                          className="form-control"
                          id="ibanNumber"
                          placeholder="e.g. SE35500000000"
                          {...register("ibanNumber", {
                            required: ["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName),
                          })}
                          style={{
                            height: "48px",
                            borderRadius: "8px",
                            border: `1px solid ${errors.ibanNumber ? "#fc8181" : "#e2e8f0"}`,
                            fontSize: "14px",
                            color: "#2d3748",
                            padding: "0 16px",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                            transition: "all 0.2s ease",
                            outline: "none"
                          }}
                        />
                      </div>
                      <div style={{flex: "1 1 calc(50% - 8px)", minWidth: "200px"}}>
                        <label
                          htmlFor="bicNumber"
                          style={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#4a5568",
                            marginBottom: "8px",
                            display: "block",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                          }}
                        >
                          {t("BIC Number")}
                        </label>
                        <input
                          className="form-control"
                          type="text"
                          id="bicNumber"
                          placeholder="e.g. ESSESESS"
                          {...register("bicNumber", {
                            required: ["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName),
                          })}
                          style={{
                            height: "48px",
                            borderRadius: "8px",
                            border: `1px solid ${errors.bicNumber ? "#fc8181" : "#e2e8f0"}`,
                            fontSize: "14px",
                            color: "#2d3748",
                            padding: "0 16px",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                            transition: "all 0.2s ease",
                            outline: "none"
                          }}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{flex: "1 1 calc(50% - 8px)", minWidth: "200px"}}>
                        <label htmlFor="inputCity" style={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#4a5568",
                          marginBottom: "8px",
                          display: "block",
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                        }}>{t("Clearing number")}</label>
                        <input
                          className="form-control"
                          type="number"
                          id="clearingNumber"
                          placeholder="e.g. 12345"
                          {...register("clearingNumber", {
                            required: !["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName),
                            validate: validateClearingNumber,
                          })}
                          onInput={(e: any) => {
                            let value = e.target.value.replace(/\D/g, "");
                            if (value.length > 5) {
                              value = value.substring(0, 5);
                            }
                            e.target.value = value;
                            validateClearingNumber(e.target.value);
                          }}
                          style={{
                            height: "48px",
                            borderRadius: "8px",
                            border: `1px solid ${errors.clearingNumber ? "#fc8181" : "#e2e8f0"}`,
                            fontSize: "14px",
                            color: "#2d3748",
                            padding: "0 16px",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                            transition: "all 0.2s ease",
                            outline: "none"
                          }}
                        />
                        {errors.clearingNumber && (
                          <div style={{
                            color: "#e53e3e",
                            fontSize: "12px",
                            marginTop: "6px",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                          }}>
                            {errors.clearingNumber.message as string}
                          </div>
                        )}
                      </div>

                      <div style={{flex: "1 1 calc(50% - 8px)", minWidth: "200px"}}>
                        <label htmlFor="inputState" style={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#4a5568",
                          marginBottom: "8px",
                          display: "block",
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                        }}>{t("Account number")}</label>
                        <input
                          type="number"
                          placeholder="e.g. 1234567890"
                          className="form-control"
                          id="accountNumber"
                          {...register("accountNumber", {
                            required: !["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName),
                            validate: validateAccountNumber,
                          })}
                          onInput={(e: any) => {
                            let value = e.target.value.replace(/\D/g, "");
                            if (value.length > 10) {
                              value = value.substring(0, 10);
                            }
                            e.target.value = value;
                            validateAccountNumber(e.target.value);
                          }}
                          style={{
                            height: "48px",
                            borderRadius: "8px",
                            border: `1px solid ${errors.accountNumber ? "#fc8181" : "#e2e8f0"}`,
                            fontSize: "14px",
                            color: "#2d3748",
                            padding: "0 16px",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                            transition: "all 0.2s ease",
                            outline: "none"
                          }}
                        />
                        {errors.accountNumber && (
                          <div style={{
                            color: "#e53e3e",
                            fontSize: "12px",
                            marginTop: "6px",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                          }}>
                            {errors.accountNumber.message as string}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && (
                  <p style={{
                    marginTop: "12px",
                    marginBottom: "24px",
                    padding: "12px 16px",
                    backgroundColor: "#f7fafc",
                    borderLeft: "4px solid #667eea",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#4a5568",
                    lineHeight: "1.5",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}>
                    {t(
                      "Note: Clearing number should be between 4-5 digits and account number can have maximum 10 digits. Special characters are not allowed."
                    )}
                  </p>
                )}
              {/* Buttons */}
              <div style={{
                display: "flex", 
                justifyContent: showBankInfoPopup ? "flex-end" : "flex-start", 
                gap: "12px",
                marginTop: "24px",
                paddingTop: showBankInfoPopup ? "20px" : "0",
                borderTop: showBankInfoPopup ? "1px solid #e2e8f0" : "none"
              }}>
                {showBankInfoPopup && (
                  <Button
                    onClick={() => {
                      setShowBankInfoPopup(false);
                    }}
                    style={{
                      backgroundColor: "transparent",
                      border: "2px solid #6750b8",
                      color: "#544196",
                      borderRadius: "25px",
                      padding: "8px 16px",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: "14px",
                      letterSpacing: "0.1px",
                      lineHeight: "20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                      transition: "all 0.2s ease"
                    }}
                  >
                    {t("Close")}
                  </Button>
                )}
                <Button
                  disabled={isSaveDisabled}
                  type="submit"
                  style={{
                    backgroundColor: "#6750b8",
                    border: "none",
                    borderRadius: "25px",
                    padding: "8px 16px",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "14px",
                    letterSpacing: "0.1px",
                    lineHeight: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: isSaveDisabled ? "not-allowed" : "pointer",
                    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.04)",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    transition: "all 0.2s ease",
                    opacity: isSaveDisabled ? 0.5 : 1
                  }}
                >
                  {t("Save Settings")}
                </Button>
              </div>
              {!showBankInfoPopup && (
                <div className="insurance-group" style={{
                  backgroundColor: "#f7fafc",
                  padding: "20px 24px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  marginTop: "24px"
                }}>
                  <h2 className="pb-0" style={{ 
                    fontSize: "16px", 
                    fontWeight: 700, 
                    fontFamily: "CircularStd-Bold, sans-serif", 
                    marginBottom: "8px",
                    color: "#1a202c"
                  }}>
                    {t("Get paid within one hour with Revolut.")}
                  </h2>
                  <p className="first" style={{
                    fontSize: "14px", 
                    fontWeight: 400, 
                    fontFamily: "CircularStd-Book, sans-serif", 
                    marginTop: "0px",
                    marginBottom: "0px",
                    color: "#4a5568",
                    lineHeight: "1.6"
                  }}>
                    <span>
                      {t("If you already have a Revolut account, simply update your bank details in the platform, and we'll pay your salary to your Revolut account. Don't have one?")}</span> <span>  <a
                      href="https://revolut.com/referral/?referral-code=kevindtxd!JUN1-25-AR-H2&geo-redirect"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "underline",
                        color: "#667eea",
                        fontWeight: 600,
                        transition: "color 0.2s ease"
                      }}
                    > {t("Sign up here")} </a></span>  <span>{t("and start benefiting today.")}</span>
                  </p>
                </div>
              )}
            </form>
          </div>
          {!showBankInfoPopup && (
            <div className="data-table">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t("Bank Name")}</th>
                    <th>
                      {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && data?.ibanNumber
                        ? t("IBAN Number")
                        : t("Clearing number")}
                    </th>
                    <th>
                      {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && data?.bicNumber
                        ? t("BIC Number")
                        : t("Account number")}
                    </th>
                    <th>{t("Income Tax")}</th>
                    <th>{t("Created Date")}</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>{t(data?.bank)}</td>
                    <td>
                      {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && data?.ibanNumber
                        ? data?.ibanNumber
                        : data?.clearingNumber}
                    </td>
                    <td>
                      {["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName) && data?.bicNumber
                        ? data?.bicNumber
                        : data?.accountNumber}
                    </td>
                    <td>
                      {data?.incomeTax}
                      {data?.incomeTax ? "%" : ""}
                    </td>
                    <td>{data?.createdDate?.slice(0, 10)}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}
      <BankOTPModal
        OTPModal={OTPModal}
        setOTPModal={setOTPModal}
        resendOTP={resendOTP}
        OTPField={OTPField}
        setOTPField={setOTPField}
        inputRefs={inputRefs}
        handleChangeOTP={handleChangeOTP}
        counter={counter}
        setCounter={setCounter}
        verifyEmail={null}
        newEmail={null}
        openInModal={!isPopup}
      />
    </div>
  );
};

export default memo(NewBankInformation);
